import {
  ACCESS_GROUP_SYNC_ACTION,
  ACCESS_GROUP_SYNC_STATUS,
  ACCESS_GROUP_SYNC_STATUS_LABELS
} from '../config/accessGroupSync';
import { formatDataverseRecordKey } from '../lib/dataverseRecordId';

const SYNC_SELECT =
  'oc_accessgroupsyncid,oc_name,oc_action,oc_status,oc_errormessage,oc_payloadjson,createdon,_oc_tilgangsgruppe_value';

function mapSyncRecord(record) {
  const status = record.oc_status;
  return {
    id: record.oc_accessgroupsyncid,
    syncId: record.oc_accessgroupsyncid,
    name: record.oc_name,
    action: record.oc_action,
    status,
    statusLabel: ACCESS_GROUP_SYNC_STATUS_LABELS[status] || String(status),
    errorMessage: record.oc_errormessage || null,
    payloadJson: record.oc_payloadjson || null,
    createdOn: record.createdon || null,
    groupId: record._oc_tilgangsgruppe_value || null
  };
}

function buildSyncName({ action, groupName }) {
  const label =
    action === ACCESS_GROUP_SYNC_ACTION.CREATE_GROUP
      ? 'Opprett'
      : action === ACCESS_GROUP_SYNC_ACTION.UPDATE_GROUP_META
        ? 'Metadata'
        : action === ACCESS_GROUP_SYNC_ACTION.DELETE_GROUP
          ? 'Slett'
          : 'Medlemskap';
  return `${label}: ${groupName || 'Tilgangsgruppe'}`;
}

export function createAccessGroupSyncService(dataverseApiCall) {
  const getStatus = async (syncId) => {
    const response = await dataverseApiCall(
      `/oc_accessgroupsyncs(${formatDataverseRecordKey(syncId)})?$select=${SYNC_SELECT}`
    );
    return mapSyncRecord(response);
  };

  return {
    create: async ({ action, groupId, groupName, payload }) => {
      const body = {
        oc_name: buildSyncName({ action, groupName }),
        oc_action: action,
        oc_status: ACCESS_GROUP_SYNC_STATUS.PENDING,
        'oc_Tilgangsgruppe@odata.bind': `/oc_tilgangsgruppes(${formatDataverseRecordKey(groupId)})`
      };

      if (payload) {
        body.oc_payloadjson = typeof payload === 'string' ? payload : JSON.stringify(payload);
      }

      const response = await dataverseApiCall('/oc_accessgroupsyncs', {
        method: 'POST',
        headers: { Prefer: 'return=representation' },
        body: JSON.stringify(body)
      });

      return mapSyncRecord(response);
    },

    listByGroup: async (groupId, { top = 20 } = {}) => {
      const key = formatDataverseRecordKey(groupId);
      const response = await dataverseApiCall(
        `/oc_accessgroupsyncs?$select=${SYNC_SELECT}` +
          `&$filter=_oc_tilgangsgruppe_value eq ${key}&$orderby=createdon desc&$top=${top}`
      );
      return (response.value || []).map(mapSyncRecord);
    },

    getStatus,

    waitForCompletion: async (syncId, { intervalMs = 2500, timeoutMs = 120000 } = {}) => {
      const started = Date.now();

      while (Date.now() - started < timeoutMs) {
        const status = await getStatus(syncId);

        if (status.status === ACCESS_GROUP_SYNC_STATUS.COMPLETED) {
          return status;
        }

        if (status.status === ACCESS_GROUP_SYNC_STATUS.FAILED) {
          throw new Error(status.errorMessage || 'Synkronisering av tilgangsgruppe feilet.');
        }

        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }

      throw new Error('Tidsavbrudd: synkronisering ble ikke ferdig behandlet i tide.');
    }
  };
}
